import {noop} from '@benchling/js-utils';
import React from 'react';

import * as analytics from 'analytics/Analytics';
import {DataModificationsHeader} from 'audit/DataModifications';
import {callAsyncWithDataModifications} from 'audit/DataModificationsModal';
import BenchlingConfig from 'BenchlingConfig';
import {FixedPlateModalType} from 'constants/analytics-products/SamplesAnalyticsConstants';
import {
  ActionOrigin,
  ANALYTICS_MODULE,
  COMMON_ANALYTICS_ACTIONS,
  STORABLE_ANALYTICS_ACTIONS,
} from 'constants/AnalyticsConstants';
import {AccessibleGridCell, FullyAccessibleGrid} from 'constants/SampleConstants';
import notifications from 'notifications';
import {
  getInventoryRegistryChangeSummary,
  InventoryRegistryDataModAction,
} from 'samples/getInventoryRegistryChangeSummary';
import {AnnotateFixedPlateWellInput, annotatePlate} from 'samples/GridDAO';
import {usePlateMapFromGrid} from 'samples/grids/hooks/usePlateMapFromGrid';
import AnnotatePlateModal from 'samples/plate-maps/annotate-plate-modal/AnnotatePlateModal';
import {getAnnotateFixedPlateInputsFromPlateMap} from 'samples/plate-maps/AnnotateFillPlateUtil';
import {PlateMap, PlateModalAnalyticsMetadata} from 'samples/plate-maps/PlateMapConstants';
import {getCellsAsIndices} from 'samples/SampleUtil';
import {bulkFetchStorables} from 'samples/StorableActions';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import runModal from 'ui-library/runModal';
import {withLoadingScreen} from 'util/Promises';

export interface GridCellToolbarAnnotateFixedPlateButtonProps {
  grid: FullyAccessibleGrid;
  selectedCells: Array<AccessibleGridCell>;
}

/**
 * Button for opening the annotate well plate modal flow.
 */
export default function GridCellToolbarAnnotateFixedPlateButton({
  grid,
  selectedCells,
}: GridCellToolbarAnnotateFixedPlateButtonProps): JSX.Element | null {
  const {plateMap, isPending} = usePlateMapFromGrid(grid);

  async function annotatePlateAndRefetch({
    callbackOnError,
    dataModsHeader,
    eventGroupId,
    gridId,
    updateData,
  }: {
    callbackOnError: () => void;
    dataModsHeader?: DataModificationsHeader;
    eventGroupId: string;
    gridId: string;
    updateData: Array<AnnotateFixedPlateWellInput>;
  }): Promise<void> {
    try {
      await annotatePlate(gridId, {annotations: updateData}, dataModsHeader);
    } catch (err) {
      callbackOnError();
      throw err;
    }

    notifications.success('Plate annotations saved');
    const analyticsMetadata: PlateModalAnalyticsMetadata = {
      actionOrigin: ActionOrigin.INVENTORY_PLATE_PAGE_GRID_CELL_TOOLBAR,
      modalType: FixedPlateModalType.ANNOTATE_PLATE,
      plateId: gridId,
      eventGroupId,
    };
    analytics.trackEvent(STORABLE_ANALYTICS_ACTIONS.SAVED_FIXED_PLATE_FROM_MODAL, analyticsMetadata);
    bulkFetchStorables([gridId]);
  }

  async function savePlateAnnotationsWithDataMods({
    annotatedSchemaFieldIds,
    callbackOnError,
    eventGroupId,
    updatedPlateMap,
  }: {
    annotatedSchemaFieldIds: Set<string>;
    callbackOnError: () => void;
    eventGroupId: string;
    updatedPlateMap: PlateMap;
  }): Promise<void> {
    const storableIds: Array<string> = updatedPlateMap.wells.map(
      (well) => grid.cells[well.position.row][well.position.column]!.container.id
    );
    const updateData = getAnnotateFixedPlateInputsFromPlateMap({
      plateMap: updatedPlateMap,
      annotatedDataParameterIds: null,
      annotatedSchemaFieldIds,
      // Measurement layers aren't applicable to plates (only plate designs).
      includeMeasurementLayers: false,
    });
    await callAsyncWithDataModifications({
      changeSummary: getInventoryRegistryChangeSummary({
        apiIds: storableIds,
        action: InventoryRegistryDataModAction.MODIFY,
      }),
      onPerformAction: async (dataModsHeader?) => {
        await withLoadingScreen(
          annotatePlateAndRefetch({
            updateData,
            gridId: grid.id,
            eventGroupId,
            dataModsHeader,
            callbackOnError,
          })
        );
      },
      onModalCancel: noop,
      apiIds: storableIds,
      analyticsParams: {
        module: 'SAMPLES',
        actionOrigin: ActionOrigin.INVENTORY_PLATE_PAGE_GRID_CELL_TOOLBAR,
        modifiedAction: InventoryRegistryDataModAction.MODIFY,
        numModifiedTotalItems: storableIds.length,
        entryId: null,
      },
      useProjectLevelDataModsLogic: BenchlingConfig.AUDITING_DATA_MODIFICATIONS_V2_INVENTORY,
    });
  }

  function runAnnotatePlateModal(): void {
    const eventGroupId = analytics.makeAnalyticsEventId();
    const analyticsMetadata: PlateModalAnalyticsMetadata = {
      actionOrigin: ActionOrigin.INVENTORY_PLATE_PAGE_GRID_CELL_TOOLBAR,
      modalType: FixedPlateModalType.ANNOTATE_PLATE,
      plateId: grid.id,
      eventGroupId,
    };
    analytics.trackEventWithModule(
      COMMON_ANALYTICS_ACTIONS.OPENED_MODAL,
      ANALYTICS_MODULE.SAMPLES,
      analyticsMetadata
    );
    runModal(AnnotatePlateModal, {
      existingPlateMap: plateMap,
      initialSelectedWellIndices: new Set(getCellsAsIndices(selectedCells, grid)),
      plateSchemaId: grid.storableSchema.id,
      cellContainerSchemaId: grid.storableSchema.containerSchemaId!,
      savePlateAnnotations: async ({
        annotatedSchemaFieldIds,
        callbackOnError,
        plateMap,
      }: {
        annotatedSchemaFieldIds: Set<string>;
        callbackOnError: () => void;
        plateMap: PlateMap;
      }) => {
        await savePlateAnnotationsWithDataMods({
          annotatedSchemaFieldIds,
          callbackOnError,
          eventGroupId,
          updatedPlateMap: plateMap,
        });
      },
      eventGroupId,
      onRequestClose: () => {
        analytics.trackEventWithModule(
          COMMON_ANALYTICS_ACTIONS.CLOSED_MODAL,
          ANALYTICS_MODULE.SAMPLES,
          analyticsMetadata
        );
      },
    });
  }

  return (
    <Button
      buttonStyle={ButtonStyle.TERTIARY_GRAY}
      onClick={runAnnotatePlateModal}
      size={ButtonSize.SMALL}
      text="Annotate plate"
      disabled={isPending}
      dataTestAttrs={{component: 'Button', key: 'annotatePlate'}}
    />
  );
}
